import { acceptHMRUpdate, defineStore } from "pinia";
import {
  CustomerAddressType,
  type CustomerAddressItem,
  type RawCustomerAddressItem,
} from "~/types/ecom/address.type";

import type { Product } from "~/types/ecom/product/product.type";


export const useAccountStore = defineStore("account", {
  state: () => ({
    profile: <any>null,
    addresses: <CustomerAddressItem[] | null>null,
    profile_address_form_visible: false,
    is_saving_profile_address: false,
    is_addresses_updating: false,
    is_searching: false,

    // credit limit form field
    is_credit_limit_form_filled: false,
    limit_options: [],
    credit_limit_error: "",
    is_credit_checkbox: false,
    credit_limit_form: {
      customer_id: "",
      customer_firstname: "",
      customer_lastname: "",
      company_name: "",
      company_registration_no: "",
      authorized_signatory: "",
      current_limit: "",
      requested_limit: "",
      email: "",
      mobile: "",
    },

    products: <Product[]>[],
    is_product_fetching: false,
    search_query: "",
    search_query_order: "",
    current_page: 1,
    pro_card_order_label: "",

    pro_card_orders_data: [],
    pro_card_order_search_query: "",
    orders_current_page: 0,
    selected_pro_card_order_ids: [],
    hide_pro_card_orders: true,
    all_pro_card_orders_selected: false,
    selected_label_order_id: "",
    selected_label: "",

    // 3 | edit username state below
    profile_edit_user_name: "",
    profile_edit_confirm_user_name: "",
    profile_edit_update_email: false,
    open_edit_username_modal: false,
    stock_notifications: [],
    is_notification_fetching: true,
    redirection_link: "",
    is_orders_fetching: true,
    is_addresses_fetching: true,
    is_profile_fetching: true,
    is_profile_save_button_loading: false,
    is_profile_save_button_disabled: false,
    is_update_password_button_loading: false,
    is_profile_delete_success: false,
    account_delete_modal: false,
    orders: [],
    selected_order_ids: [],
    profile_title_options: [],
    change_password_inputs: {
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
    loading: false,
    errors: {
      password: "",
      new_password: "",
      new_password_confirmation: "",
      email: "",
      current_limit: "",
      company: "",
      company_registration_no: "",
      mobile: "",
      telephone: "",
      first_name: "",
      last_name: "",
    },
    is_current_password_verified: false,
    orders_response: null,
    trade_account_value: "Trade Credit",
    order_query: {
      page: 1,
      page_limit: 5,
    },
    contact_preferences: null,
    preference_order: [],
    preferred_language: [],
    // State for messages
    messages: [],
    is_messages_fetching: true,
    messages_error: null,
  }),
  getters: {
    primaryAddress: (state) =>
      state.addresses?.find(
        (address) => address.type === CustomerAddressType.PRIMARY
      ),
  },
  persist: [
    {
      key: "increase-credit-form-filled",
      pick: ["is_credit_limit_form_filled"],
    },
  ],
  actions: {
    async initialize(){
      try {
        const data = await import('~/json/account.json');
        this.preference_order = data.preferenceOrder;
        this.preferred_language = data.preferredLanguage;
        this.limit_options = data.limitOptions;
      } catch (error) {
        console.error("Error loading preferences:", error);
      }
    },

    async fetchStockNotifications() {
      const { getStockNotifications } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await getStockNotifications();
    },

    async fetchMessages(customerId: string) {
      const { getMessages } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await getMessages(customerId);
    },

    async markAsReadMessages(customerId: String, messageIds: Array<string>) {
      const { handleReadMessages } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleReadMessages(customerId, messageIds);
    },

    async getSavedAddresses(customer_id?: string, query?: object) {
      const { fetchSavedAddresses } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await fetchSavedAddresses(customer_id, query);
    },

    async addAddress(
      customer_id?: string,
      customer_token?: string,
      address: RawCustomerAddressItem = {
        type: CustomerAddressType.DELIVERY,
        line_1: "Sec 21",
        line_2: "Karol Bagh",
        line_3: "New Delhi",
        town: "Delhi",
        county: "India",
        postcode: "110051",
        country_id: 8,
      },
      showToastNotification = true,
      query?: object
    ) {
      const { handleAddNewAddress } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleAddNewAddress(customer_id, customer_token, address, showToastNotification, query);
    },

    async setAddressAsPrimary(address_id: string, customer_id?: string, query?: object) {
      const { makeAddressAsPrimary } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await makeAddressAsPrimary(address_id, customer_id, query);
    },

    closeModal() {
      this.profile_edit_user_name = "";
      this.profile_edit_confirm_user_name = "";
      this.profile_edit_update_email = false;
      this.open_edit_username_modal = false;
    },

    async updateUserName(
      name: string,
      customerId: string,
      isEmailLabelCheck: boolean
    ) {
      const { handleUpdateUsername } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleUpdateUsername(name, customerId, isEmailLabelCheck);
    },

    async deleteAddress(address_id: string, customer_id?: string, query?: object) {
      const { handleDeleteAddress } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleDeleteAddress(address_id, customer_id, query);
    },

    async updateAddress(options: {
      customer_id?: string;
      address_id: string;
      new_address: CustomerAddressItem;
      query?: object
    }) {
      const { handleUpdateAddress } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleUpdateAddress(options);
    },

    checkIfAddressAlreadyPresent(new_address_data: any) {
      if (!this.addresses || !this.addresses.length) return false;
      if (!isCustomerAddressPresent(this.addresses, new_address_data)) return false;

      import(
        "~/services-optimized/toastService"
      ).then(({toastErrors}) => 
        toastErrors([useTranslation("Toast-addressAlreadyPresent", "Adres al aanwezig. Controleer aub")], "top-right")
      );
      return true;
    },

    async getOrders(page = 1) {
      const { fetchOrders } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await fetchOrders(page);
    },

    async getOrderInvoice(
      order_id: string,
      label: string,
      stream_response = true,
      full_invoice = true
    ) {
      const { fetchOrderInvoice } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await fetchOrderInvoice(order_id, label, stream_response, full_invoice);
    },

    async getProCardOrdersInvoice(
      order_id: string,
      label: string,
      stream_response = true,
      full_invoice = true
    ) {
      const { fetchProCardOrderInvoice } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await fetchProCardOrderInvoice(
        order_id,
        label,
        stream_response,
        full_invoice
      );
    },

    async updateContactPreferences(options?: { preferenceData?: Record<string, any>, customerId?: string, customerToken?: string, showToastNotification?: boolean }) {
      const { preferenceData, customerId, customerToken, showToastNotification } = options || {};
      const { handleContactPreferences } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleContactPreferences(preferenceData, customerId, customerToken, showToastNotification);
    },

    async getLoyaltyClub() {
      const { fetchLoyaltyClub } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await fetchLoyaltyClub();
    },

    async downloadFile(blob, fileName) {
      const { handleFileDownload } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleFileDownload(blob, fileName);
    },

    async downloadInvoices() {
      const { handleDownloadInvoices } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleDownloadInvoices();
    },

    async downloadOrderHistoryInvoices() {
      const { handleDownloadOrderHistoryInvoices } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleDownloadOrderHistoryInvoices();
    },
    //----------------------------------------------------------
    toLabel(str: string) {
      if (typeof str === "string") {
        str = str.replace(/_/g, " ");
        str = this.toUpperCaseWords(str);
        return str;
      }
    },
    //----------------------------------------------------------
    toUpperCaseWords(str: string) {
      if (str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
      }
    },
    //----------------------------------------------------------
    async updateCustomer() {
      const { handleUpdateCustomer } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleUpdateCustomer();
    },

    validateEmailFormat(email: string) {
      if (typeof email !== "string") return false;

      const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      return emailPattern.test(email);
    },
    //----------------------------------------------------------
    async verifyCurrentPassword(e) {
      const { handlePasswordVerify } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handlePasswordVerify(e);
    },
    //----------------------------------------------------------
    async changePassword(e) {
      const { handleChangePassword } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleChangePassword(e);
    },

    async deleteCustomer() {
      const { handleDeleteCustomer } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleDeleteCustomer();
    },

    async handleTrackOrderHistory(orderId: string) {
      const { handleOrderHistoryTrack } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleOrderHistoryTrack(orderId);
    },

    async fetchProCardOrdersHistory(customerId: string) {
      const { getProCardOrderHistory } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await getProCardOrderHistory(customerId);
    },

    async updateProCardOrdersData(data: object) {
      const { handleUpdateProCardOrders } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleUpdateProCardOrders(data);
    },
    async submitCreditIncreaseForm() {
      const { handleSubmitCreditIncForm } = await import(
        "~/stores-datalayer/account.datalayer"
      );
      return await handleSubmitCreditIncForm();
    },
    resetProfileFormValidation() {
      this.errors = {
        password: "",
        new_password: "",
        new_password_confirmation: "",
        email: "",
        current_limit: "",
        company: "",
        company_registration_number: "",
        mobile: "",
        telephone: "",
        first_name: "",
        last_name: "",
      };
    },
    resetCreditIncreaseFormDetails() {
      this.credit_limit_form.company_registration_number = "";
      this.credit_limit_form.company_name = "";
      this.credit_limit_form.customer_name = "";
      this.credit_limit_form.current_limit = "";
      this.credit_limit_form.current_limit = "";
      this.credit_limit_form.requested_limit = "";
      this.credit_limit_form.is_credit_checkbox = false;
      this.is_credit_limit_form_filled = false;
    },
  },
});

// Pinia hot reload
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAccountStore, import.meta.hot));
}